import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './components/LoginPage';
import DashboardLayout from './components/DashboardLayoutErp';
import RoleProtectedRoute from './components/RoleProtectedRoute';

// === Admin Pages ===
import AdminHome from './pages/admin/AdminDashboardHome';
import ManageUsers from './pages/admin/ManageUsers';
import HrModule from './pages/admin/HrModule';
import InventoryManagement from './pages/admin/InventoryManagement';

// === Staff Pages ===
import StaffHome from './pages/staff/StaffDashboardHome';
import Billing from './pages/staff/UnifiedBilling';
import PettyCash from './pages/staff/PettyCash';

// === Student Pages ===
import StudentHome from './pages/student/StudentHome';
import Assignments from './pages/student/Assignments';
import Admission from './pages/staff/Admission';
import ManageStudents from './pages/staff/ManageStudents';
import ManageStaff from './pages/admin/ManageStaff';
import LeaveAndSalary from './pages/staff/LeaveAndSalary';
import ClientManagement from './pages/admin/ClientManagement';
import ClientDetails from './pages/admin/ClientDetails';
import Leads from './pages/staff/Leads';
import LeadManagement from './pages/admin/LeadManagement';
import AdminTasks from './pages/admin/AdminTasks';
import MyTasks from './pages/staff/MyTasks';
import ExpenseTracker from './pages/admin/ExpenseTracker';
import AdminBatches from './pages/admin/AdminBatches';
import SendWhatsApp from './pages/admin/SendWhatsApp';
import AttendanceAdmin from './pages/admin/AttendanceAdmin';
import HiringBoard from './pages/staff/HiringBoard';
import ManageWorkshopRegistrations from './pages/admin/ManageWorkshopRegistrations';
import Interns from './pages/staff/Interns';
import CourseManagement from './pages/staff/CourseManagement';
import Reports from './pages/staff/Reports';
import ClientQueries from './pages/admin/ClientQueries';
import ClientSupport from './pages/client/ClientSupport';
function App() {
  const [user, setUser] = useState(null);
  const adminRoles = ['admin', 'Super Admin', 'Admin', 'HR', 'Trainer', 'Staff', 'Accountant', 'Marketing', 'Sales', 'Counselor', 'Reception', 'Custom', 'staff'];

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) setUser(JSON.parse(savedUser));
  }, []);

  // Default route redirection based on role
  const getDefaultRoute = (role) => {
    switch (String(role || '').toLowerCase().replaceAll(' ', '_')) {
      case 'admin':
      case 'super_admin':
        return '/dashboard/home';
      case 'staff':
        return '/dashboard/staff-home';
      case 'student':
        return '/dashboard/student-home';
      case 'client':
        return '/dashboard/client-support';
      default:
        return '/dashboard/staff-home';
    }
  };

  return (
    <Router>
      <Routes>
        {/* === LOGIN === */}
        <Route
          path="/"
          element={
            user ? (
              <Navigate to={getDefaultRoute(user.role)} replace />
            ) : (
              <LoginPage setUser={setUser} />
            )
          }
        />

        {/* === DASHBOARD LAYOUT (Shared) === */}
        <Route
          path="/dashboard/*"
          element={
            user ? (
              <DashboardLayout user={user} setUser={setUser} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        >
          {/* ADMIN ROUTES */}
          <Route
            path="home"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="dashboard">
                <AdminHome user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="manage-users"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="users">
                <ManageUsers />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="hr"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="hr">
                <HrModule user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="inventory"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="inventory">
                <InventoryManagement user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="manage-staff"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="hr">
                <ManageStaff user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="clients"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="projects">
                <ClientManagement user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="clients/:clientId"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="projects">
                <ClientDetails />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="client-queries"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="projects">
                <ClientQueries user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="lead-management"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="admission">
                <LeadManagement user={user}/>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="staff-attendance"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="attendance">
                <AttendanceAdmin user={user}/>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="tasks"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="tasks">
                <AdminTasks user={user}/>
              </RoleProtectedRoute>
            }
          />
           <Route
            path="expense-tracker"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="fees">
                <ExpenseTracker user={user}/>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="manage-batches"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="batch">
                <AdminBatches user={user}/>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="send-whatsapp"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['admin']}>
                <SendWhatsApp user={user}/>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="workshop-registrations"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="admission">
                <ManageWorkshopRegistrations user={user}/>
              </RoleProtectedRoute>
            }
          />
          {/* STAFF ROUTES */}
          <Route
            path="staff-home"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="dashboard">
                <StaffHome user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="Mytasks"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="tasks">
                <MyTasks user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="hiring"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="hr">
                <HiringBoard user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="billing"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="billing">
                <Billing user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="reports"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="reports">
                <Reports user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="petty-cash"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="fees">
                <PettyCash user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="admission"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="admission">
                <Admission user={user} />
              </RoleProtectedRoute>
            }
          />
          
          <Route
  path="course-management"
  element={
    <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="courses">
      <CourseManagement user={user} />
    </RoleProtectedRoute>
  }
/>

          <Route
            path="interns"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="students">
                <Interns user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="manage-students"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="students">
                <ManageStudents user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="leave-salary"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="leave">
                <LeaveAndSalary user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="leads"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="admission">
                <Leads user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="manage-batches"
            element={
              <RoleProtectedRoute user={user} allowedRoles={adminRoles} permission="batch">
                <AdminBatches user={user}/>
              </RoleProtectedRoute>
            }
          />
          {/* STUDENT ROUTES */}
          <Route
            path="student-home"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['student']}>
                <StudentHome />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="assignments"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['student']}>
                <Assignments />
              </RoleProtectedRoute>
            }
          />
          {/* CLIENT ROUTES */}
          <Route
            path="client-support"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['client']}>
                <ClientSupport user={user} />
              </RoleProtectedRoute>
            }
          />
        </Route>

        {/* CATCH-ALL */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
